import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, FlaskRound as Flask, Tags, DollarSign, Star, AlertTriangle, Edit, ExternalLink } from 'lucide-react';
import { useInventory } from '../context/InventoryContext';
import { supabase } from '../lib/supabase';
import RDProductForm from '../components/rd/RDProductForm';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';

const RDProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getCategoryName } = useInventory();
  const [showEditForm, setShowEditForm] = useState(false);
  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  
  const isNewProduct = id === 'new';

  useEffect(() => {
    if (!isNewProduct && id) {
      loadProduct();
    } else {
      setLoading(false);
    }
  }, [id, isNewProduct]);

  const loadProduct = async () => {
    try {
      const { data, error } = await supabase
        .from('rd_inventory')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      
      if (data) {
        setProduct(data);
      }
    } catch (error) {
      console.error('Error loading R&D product:', error);
      setProduct(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  if (!isNewProduct && !product) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">R&D Product Not Found</h2>
        <p className="text-gray-500 mb-6">The R&D product you're looking for doesn't exist.</p>
        <Button onClick={() => navigate('/rd-inventory')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to R&D Inventory
        </Button>
      </div>
    );
  }

  if (isNewProduct || showEditForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/rd-inventory')}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">
            {isNewProduct ? 'Add New R&D Product' : `Edit R&D Product: ${product?.name}`}
          </h1>
        </div>
        
        <RDProductForm 
          product={product} 
          isEdit={!isNewProduct} 
        />
      </div>
    );
  }

  const lowStock = product && product.stock_quantity <= product.min_stock_level;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/rd-inventory')}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <Link to="/rd-inventory" className="text-sm text-gray-500 hover:text-gray-700">
                  R&D Inventory
                </Link>
              </li>
              <li>
                <span className="text-sm text-gray-600">/</span>
              </li>
              <li>
                <span className="text-sm text-gray-900">{product?.name}</span>
              </li>
            </ol>
          </nav>
        </div>
        <Button onClick={() => setShowEditForm(true)}>
          <Edit className="h-4 w-4 mr-2" />
          Edit R&D Product
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="aspect-w-16 aspect-h-9 mb-6">
              {product?.image ? (
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-64 object-contain bg-gray-50 rounded-lg"
                />
              ) : (
                <div className="w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Flask className="h-12 w-12 text-gray-600" />
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900">{product?.name}</h1>
                <div className="flex gap-2">
                  {lowStock && (
                    <Badge variant="danger">Low Stock</Badge>
                  )}
                  <Badge variant={product?.status === 'approved' ? 'success' : 'warning'}>
                    {product?.status}
                  </Badge>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Badge variant="secondary">
                  {getCategoryName(product?.category_id || '')}
                </Badge>
                <span className="text-sm text-gray-500">SKU: {product?.sku}</span>
              </div>

              <p className="text-gray-600">{product?.description}</p>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-600 mb-1">
                    <DollarSign className="h-4 w-4" />
                    <span className="text-sm">Est. Value</span>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">
                    ${product?.price?.toFixed(2)}
                  </span>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-600 mb-1">
                    <Flask className="h-4 w-4" />
                    <span className="text-sm">Stock</span>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">
                    {product?.stock_quantity}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>R&D Project Details</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="divide-y divide-gray-200">
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Project Code</dt>
                  <dd className="text-sm text-blue-600 font-medium col-span-2">
                    {product?.project_code}
                  </dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Researcher</dt>
                  <dd className="text-sm text-gray-900 col-span-2">{product?.researcher}</dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Phase</dt>
                  <dd className="text-sm text-gray-900 col-span-2">{product?.phase}</dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Location</dt>
                  <dd className="text-sm text-gray-900 col-span-2">{product?.location}</dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Development Cost</dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    ${product?.cost_price?.toFixed(2)}
                  </dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Min Stock Level</dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    {product?.min_stock_level}
                  </dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Target Completion</dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    {product?.target_completion ? new Date(product.target_completion).toLocaleDateString() : 'TBD'}
                  </dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Budget Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-500">Budget Allocated:</span>
                  <span className="text-sm font-bold text-gray-900">
                    ${product?.budget_allocated?.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-500">Budget Spent:</span>
                  <span className="text-sm font-bold text-gray-900">
                    ${product?.budget_spent?.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-500">Remaining:</span>
                  <span className="text-sm font-bold text-green-600">
                    ${((product?.budget_allocated || 0) - (product?.budget_spent || 0)).toLocaleString()}
                  </span>
                </div>
                
                {/* Budget Progress Bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Budget Usage</span>
                    <span>
                      {Math.round(((product?.budget_spent || 0) / (product?.budget_allocated || 1)) * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ 
                        width: `${Math.min(((product?.budget_spent || 0) / (product?.budget_allocated || 1)) * 100, 100)}%` 
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {lowStock && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3 text-amber-600">
                  <AlertTriangle className="h-5 w-5" />
                  <div>
                    <p className="font-medium">Low Stock Alert</p>
                    <p className="text-sm">
                      Current stock ({product?.stock_quantity}) is below minimum level ({product?.min_stock_level})
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default RDProductDetailPage;